import { useCallback, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * useState that is also saved on the current browser history entry.
 *
 * Coming back to the page with the browser back button restores the saved
 * value. Arriving any other way (breadcrumb, menu link, typed URL) creates a
 * new history entry with no saved value, so the page starts from
 * `initialValue`.
 *
 * @param {string} key name the value is saved under on the history entry
 * @param {*} initialValue value used when the history entry has nothing saved
 * @returns {[*, (value: *) => void]} [ value, setValue ]
 */
const useHistoryState = (key, initialValue) => {
  const location = useLocation();
  const navigate = useNavigate();
  const locationRef = useRef(location);
  locationRef.current = location;

  const [value, setValue] = useState(() =>
    location.state?.[key] === undefined ? initialValue : location.state[key],
  );

  const saveValue = useCallback(
    (nextValue) => {
      setValue(nextValue);
      const { pathname, search, hash, state } = locationRef.current;
      navigate(`${pathname}${search}${hash}`, {
        replace: true,
        state: { ...state, [key]: nextValue },
      });
    },
    [key, navigate],
  );

  return [value, saveValue];
};

export default useHistoryState;
